import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleTag from "_shared/fee-schedules/tags/fee-schedule-tag";
import Guid from "_shared/guids/guid";
import { FeeScheduleCategoryMaskedCurrency } from "work/masked-fields";
import FeeScheduleTemplateSchema, {
  FeeScheduleTemplateJSON,
  FeeScheduleTemplateParams,
} from "work/fee-schedule-templates/_schemas/fee-schedule-template-schema";

export default class FeeScheduleTemplate {
   public static get EXAMPLE(): FeeScheduleTemplate {
    return new FeeScheduleTemplate({
      id: Guid.generate(),
      name: "Example Fee Schedule Template",
      tags: [new FeeScheduleTag("example")],
      categories: [FeeScheduleCategory.EXAMPLE],
      created: new AHDate(),
    });
  }

  public static create(params: FeeScheduleTemplateParams): FeeScheduleTemplate {
    const parsed = FeeScheduleTemplateSchema.validate(params);
    return new FeeScheduleTemplate({ ...parsed, ...params });
  }

  public static fromJSON(json: FeeScheduleTemplateJSON): FeeScheduleTemplate {
    const templateParams = FeeScheduleTemplateSchema.deserialize(json);
    return new FeeScheduleTemplate(templateParams);
  }

  public static fromJSONArray(jsonArray: FeeScheduleTemplateJSON[]): FeeScheduleTemplate[] {
    return jsonArray.map((json) => FeeScheduleTemplate.fromJSON(json));
  }

  public readonly id?: Guid;
  public readonly version: number;
  public readonly creator?: EntityMember;
  public readonly created?: AHDate;
  public readonly name: string;
  public readonly tags: FeeScheduleTag[] = [];
  public readonly categories: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] = [];
  public readonly archived?: AHDate;

  constructor(params: FeeScheduleTemplateParams) {
    this.id = params.id;
    this.version = params.version ?? 0;
    this.created = params.created;
    this.creator = params.creator ? EntityMember.create(params.creator) : undefined;
    this.name = params.name;
    this.tags = params.tags ?? [];
    this.categories =
      params.categories?.map((category) =>
        category.currencyIsMasked
          ? FeeScheduleCategoryMaskedCurrency.create(category)
          : FeeScheduleCategory.create(category)
      ) ?? [];
    this.archived = params.archived;
  }

  public toJSON(): FeeScheduleTemplateJSON {
    return FeeScheduleTemplateSchema.serialize(this);
  }

  // A proposal's fee schedule holds category VALUE SNAPSHOTS, not catalog references: a category
  // added through the fee-schedule add panel gets a freshly minted id even when it started life as
  // one of this template's categories (see draft-proposal-fee-schedule-tab.tsx's categorySource
  // comment). Matching by id would then never recognize it as "from this template," so identity here
  // is by name, matching every other category-membership check in that file (handleCategoryRemoved,
  // the template-selection dedup).
  public isUsingCategories(categories: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[]): boolean {
    return categories.some((category) => {
      return this.categories.some((thisCategory) => category.name?.isEqualTo(thisCategory.name));
    });
  }
}
